import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useAuth } from '@/contexts/AuthContext'
import { affiliateDetailsApi, payoutsApi } from '@/services/api'
import { evaluateAffiliateCompleteness } from '@/utils/affiliateCompleteness'

/**
 * #629 / #1321 — Sticky top warning shown ONLY to affiliate-role users whose
 * "Company & Bank Details" are incomplete (mirrors the impersonation banner's
 * placement). Links to /affiliate-details to fill the gaps.
 *
 * #1321 — this used to decide from the CANONICAL stores alone (the
 * affiliate_billing row behind /billing, and the bank_accounts rows behind
 * /bank-accounts). Company and bank data captured during onboarding never
 * reaches either: it lands on affiliates.company_* and the legacy
 * affiliates.bank_account_details JSONB. AffiliateDetailsPage has read those
 * fallbacks since #839, so the banner claimed details were missing while the
 * page it linked to displayed them. On staging that was every affiliate with
 * bank details on file. The completeness rules now live in one shared helper
 * that both this banner and the page call, so they cannot drift again.
 *
 * Scope notes:
 *  - Affiliates ONLY. An admin session never sees this, even one that happens
 *    to own an affiliate: an admin reviewing someone else's profile must not
 *    be warned about their own. Under impersonation the session IS the
 *    affiliate, so the banner evaluates that affiliate's real data and shows
 *    only when it is genuinely incomplete.
 *  - Hotel users have no affiliate association, so the affiliateId gate keeps
 *    this off every hotel page.
 *  - The viewer is always the owner of their own affiliate, so the
 *    session-scoped /bank-accounts read is safe here.
 *  - Query keys match AffiliateDetailsPage so React Query serves a single
 *    shared fetch (no duplicate requests, instant when the page is open, and
 *    the banner clears the moment a save invalidates them).
 */
export const AffiliateCompanyBankBanner = () => {
  const { t } = useTranslation('affiliates')
  const navigate = useNavigate()
  const { user } = useAuth()

  const affiliateId = user?.affiliates?.find((a) => a.isActive)?.affiliateId

  // Same admin predicate AffiliateDetailsPage uses, so the shared detail query
  // key below resolves identically for the same session.
  const isAdminSession =
    !!user?.roles?.includes('admin') ||
    !!user?.roles?.includes('global_admin') ||
    user?.userType === 'admin' ||
    user?.userType === 'global_admin'

  const enabled = !!affiliateId && !isAdminSession

  const { data: billing } = useQuery({
    queryKey: ['affiliate-billing', affiliateId],
    queryFn: async () => {
      const res = await affiliateDetailsApi.getBilling(affiliateId as string)
      return res.data ?? null
    },
    enabled,
  })

  const { data: bankAccounts } = useQuery({
    queryKey: ['affiliate-bank-accounts', affiliateId],
    queryFn: async () => {
      const res = await payoutsApi.getBankAccounts()
      return Array.isArray(res.data) ? res.data : []
    },
    enabled,
  })

  // #1321 — carries the onboarding-era company_* columns and the legacy bank
  // JSONB. Key shape (and the trailing isAdmin flag) matches the page's query.
  const { data: details } = useQuery({
    queryKey: ['affiliate-details', affiliateId, isAdminSession],
    queryFn: async () => {
      const res = await affiliateDetailsApi.getDetails(affiliateId as string)
      return res.data ?? null
    },
    enabled,
  })

  if (!enabled) return null
  // Wait until every read resolves before deciding, to avoid a flash on load.
  if (billing === undefined || bankAccounts === undefined || details === undefined) return null

  const { isIncomplete } = evaluateAffiliateCompleteness({ billing, details, bankAccounts })
  if (!isIncomplete) return null

  return (
    <div role="alert" className="bg-red-50 border-b-2 border-red-300 px-4 py-2.5 text-red-800">
      <div className="container mx-auto flex items-center justify-center gap-3 text-center">
        <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-600" aria-hidden="true" />
        <span className="text-sm font-medium">
          {t(
            'companyBankWarning.message',
            'Your Company & Bank Details are incomplete. Complete them so you can receive payouts.',
          )}{' '}
          <button
            type="button"
            onClick={() => navigate('/affiliate-details')}
            className="font-bold underline text-red-700 hover:text-red-900"
          >
            {t('companyBankWarning.cta', 'Complete now')}
          </button>
        </span>
      </div>
    </div>
  )
}
